/**
 * List-page URL state (spec §6.3): page, page size, sort and quick search, plus each feature's
 * own filters, as typed search params. Invalid values fall back instead of breaking the page,
 * and defaults stay out of the URL so shared links are short.
 *
 * TanStack Router JSON-parses search values (`?q=123` arrives as a number), so free-text
 * params coerce to strings.
 */
import { z } from 'zod';
import type { FilterDescriptor, PagedQuery, SortDescriptor } from '@/api/paged-query';
import { createLocalStore } from './local-store';

export const PAGE_SIZES = [10, 25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 25;

const isPageSize = (n: number) => (PAGE_SIZES as readonly number[]).includes(n);

/**
 * The rows per page last picked on any list, kept in this browser: every list opens on it, after a
 * reload too. A `size` in the URL wins and becomes the stored one (`useDataTable` syncs it).
 */
export const pageSizeStore = createLocalStore('rgs.tables.pageSize', z.number().refine(isPageSize), DEFAULT_PAGE_SIZE);

export interface ListSearch {
  page?: number;
  size?: number;
  /** `Name` ascending, `-Name` descending. */
  sort?: string;
  q?: string;
}

/** `-Name` → `{field: 'Name', dir: 'desc'}`. */
export function parseSortParam(value: string | undefined): SortDescriptor | null {
  if (!value) return null;
  const desc = value.startsWith('-');
  const field = desc ? value.slice(1) : value;
  return field ? { field, dir: desc ? 'desc' : 'asc' } : null;
}

export function formatSortParam(sort: SortDescriptor | null | undefined): string | undefined {
  if (!sort) return undefined;
  return sort.dir === 'desc' ? `-${sort.field}` : sort.field;
}

const pageSchema = z.coerce.number().int().min(1).max(100_000).optional().catch(undefined);
const sizeSchema = z.coerce.number().refine(isPageSize).optional().catch(undefined);
const qSchema = z.coerce.string().trim().max(200).optional().catch(undefined);

/** A search-param filter that drops unknown values instead of failing the route. */
export const filterParam = {
  /** Numeric-looking values (`?transactions=1`) arrive as numbers, so they're read back as strings. */
  enum: <const T extends readonly [string, ...string[]]>(values: T) =>
    z
      .union([z.enum(values), z.number().transform(String).pipe(z.enum(values))])
      .optional()
      .catch(undefined),
  boolean: () =>
    z
      .union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')])
      .optional()
      .catch(undefined),
  string: () => z.coerce.string().trim().max(200).optional().catch(undefined),
  number: () => z.coerce.number().finite().optional().catch(undefined),
  /** `YYYY-MM-DD` (UTC day), e.g. the `from`/`to` of a date filter. */
  date: () =>
    z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional()
      .catch(undefined),
};

/**
 * The search schema for one list page. `sortFields` is the server's sortable allow-list: a
 * sort on anything else is dropped, because the server would silently ignore it.
 */
// Default to no extra keys: without it, a call with no `filters` falls back to the constraint's index
// signature and every key (`q` included) types as unknown.
export function listSearchSchema<TFilters extends z.ZodRawShape = Record<never, never>>(options: {
  sortFields: readonly string[];
  filters?: TFilters;
}) {
  const sortable = new Set(options.sortFields);
  const sortSchema = z
    .string()
    .refine((v) => sortable.has(v.replace(/^-/, '')))
    .optional()
    .catch(undefined);
  return z.object({
    page: pageSchema,
    size: sizeSchema,
    sort: sortSchema,
    q: qSchema,
    ...((options.filters ?? {}) as TFilters),
  });
}

/** Drops empty values and defaults so the URL only carries what differs from the default view. */
export function normalizeSearch<T extends ListSearch>(search: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null || value === '') continue;
    if (key === 'page' && value === 1) continue;
    // A size stays even at 25: the lists' default is whatever this browser picked last (pageSizeStore).
    out[key] = value;
  }
  return out as T;
}

const LIST_KEYS = new Set(['page', 'size', 'sort']);

/** Filters in effect: the quick search plus every feature filter with a value. */
export function activeFilterKeys(search: ListSearch): string[] {
  return Object.entries(search)
    .filter(([key, value]) => !LIST_KEYS.has(key) && value !== undefined && value !== '')
    .map(([key]) => key);
}

/** URL state → the server's paged query. Filters are the feature's mapping of its own params. */
export function toPagedQuery(
  search: ListSearch,
  options: { defaultSort?: SortDescriptor; filters?: FilterDescriptor[] } = {},
): PagedQuery {
  const size = search.size ?? pageSizeStore.get();
  const page = search.page ?? 1;
  return {
    skip: (page - 1) * size,
    take: size,
    sort: parseSortParam(search.sort) ?? options.defaultSort ?? null,
    filters: options.filters ?? [],
  };
}
